import moment from "moment";
import "moment/locale/es";

const formatPrice = (num) => {
  const formatted = new Intl.NumberFormat("es-MX", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(num);
  return `$${formatted}`;
};

const formatDate = (date, hours) => {
  const momDate = moment(date, "YYYY-MM-DD");
  momDate.locale("es");
  return `${momDate.format("DD/MMM/YYYY")} ${hours || ""}`.replace(".", "");
};

const formatFriendlyDate = (date, hours) => {
  const m = moment(date);
  m.locale("es");

  const today = moment().startOf("day");
  const yesterday = moment().subtract(1, "day").startOf("day");

  // Auto-detect a time component: input has "T" or a space+HH:MM, and time isn't midnight.
  // This lets full timestamps show "Hoy a las HH:MM" while bare dates ("2026-05-01") don't.
  let timeSuffix = "";
  if (hours) {
    timeSuffix = " " + hours;
  } else if (date && typeof date === "string" && /[T ]\d{2}:\d{2}/.test(date)) {
    const hhmm = m.format("HH:mm");
    if (hhmm !== "00:00") timeSuffix = ` a las ${hhmm}`;
  }

  if (m.isSame(today, "day")) {
    return `Hoy${timeSuffix}`;
  } else if (m.isSame(yesterday, "day")) {
    return `Ayer${timeSuffix}`;
  } else {
    return `${m.format("D [de] MMM YYYY")}${timeSuffix}`;
  }
};

const numberWithCommas = (x) => {
  if (!x) return;
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// Compact, no-time date for dense tables — e.g. "25/jun/26".
const formatShortDate = (date) => {
  if (!date) return "";
  const m = moment(date);
  m.locale("es");
  return m.format("DD/MMM/YY").replace(".", "");
};

// Canonical lifecycle order for the derived reservation-status labels (the ones
// produced by the search_reservations / report_saldo_a_hoy CASE and by
// Reservations.js statusLabel). Use this to order status filter choices, facet
// options, and status-column sorting everywhere so they read in a sensible flow
// instead of alphabetically. Unknown labels sort to the end.
const RESERVATION_STATUS_ORDER = [
  "Cotización",
  "Reservado",
  "Pagada",
  "Entregado",
  "Devuelto",
  "Completada",
  "Cancelada",
  "No-Show",
];

// Placa canónica: mayúsculas y sin guiones, puntos ni espacios. Las placas de
// subarrendado se capturan a mano ("PSM837E ", "RLT-722-C") y sin normalizar
// una misma unidad se parte en varios renglones de los reportes. Espejo de
// `public.norm_plate(text)` en Postgres.
const normPlate = (plate) =>
  String(plate ?? "").replace(/[^A-Za-z0-9]/g, "").toUpperCase() || null;

// Etiqueta de placa para mostrar. Un auto recién comprado aún no tiene placa
// (cars.plate es NULL hasta que se asigna en el registro vehicular), así que
// nunca mostramos "null" ni un espacio en blanco donde debería ir la placa.
const formatPlate = (plate) => {
  const p = String(plate ?? "").trim();
  return p || "Sin placa";
};

// Comparator (a, b) => number over the status labels above.
const compareReservationStatus = (a, b) => {
  const idx = (v) => {
    const i = RESERVATION_STATUS_ORDER.indexOf(v);
    return i === -1 ? RESERVATION_STATUS_ORDER.length : i;
  };
  return idx(a) - idx(b) || String(a ?? "").localeCompare(String(b ?? ""), "es");
};

export {
  formatPrice,
  formatDate,
  numberWithCommas,
  formatFriendlyDate,
  formatShortDate,
  normPlate,
  formatPlate,
  RESERVATION_STATUS_ORDER,
  compareReservationStatus,
};
